import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Building2,
  KeyRound,
  Layers,
  MapPin,
  Plus,
  Search,
  Trash2,
  Truck,
  Wallet,
  Map as MapIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { companiesAPI, trucksAPI, tripsAPI } from '../lib/api';
import { partnerAccountAPI } from '../lib/partnerAccountApi';
import { useAuth } from '../context/AuthContext';
import { formatMontant } from '../lib/formats';
import { StatutBadge } from '../components/ui/StatutBadge';
import { statMontant } from '../components/lots/paiementsUi';
import { messageErreur } from '../lib/erreurs';
import { fetchAllPages } from '../lib/fetchAllPages';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePaginationServeur } from '../hooks/usePagination';
import { useDerniereDemande } from '../hooks/useDerniereDemande';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/sonner';
import { FieldError } from '../components/ui/field-error';
import { cn, formatRelativeTime, getStatusBadgeClass } from '../lib/utils';
import { libelle, nomPays } from '../lib/libelles';
import { Badge } from '../components/ui/badge';
import { SectionCard } from '../components/dashboard';
import { PageHero } from '../components/dashboard/PageHero';
import { LoadingState } from '../components/ui/loading-state';
import { useDebouncedValue } from '../hooks/use-debounced-value';
import { EmptyState } from '../components/ui/empty-state';

/**
 * Sociétés — le réseau de partenaires transport.
 *
 * L'écran répond à trois questions dans cet ordre : combien de partenaires,
 * lesquels portent réellement de la capacité (des camions), et lequel
 * regarde-t-on en détail. Les quatre compteurs en tête ne sont donc pas quatre
 * chiffres décoratifs : « à qualifier » est le seul qui appelle une action, et
 * c'est le seul qui porte une teinte d'alerte. La couleur marque l'état, jamais
 * la position dans la ligne — la troisième tuile n'est pas verte parce qu'elle
 * est troisième, elle l'est parce qu'« en exploitation » est un bon état.
 */

const isTripActive = (status) => !['COMPLETED', 'CANCELLED'].includes(status);

/**
 * Les groupes de boutons segmentés de la barre de filtres.
 *
 * Ils sont décrits en données plutôt qu'écrits trois fois en JSX : les deux
 * groupes partagent exactement la même mécanique, et les avoir dupliqués est
 * précisément ce qui avait fait diverger leurs libellés (« Camions: Tous »
 * collé dans le premier bouton d'un côté, une étiquette séparée de l'autre).
 */
const GROUPES_FILTRE = [
  {
    cle: 'camions',
    etiquette: 'Camions',
    options: [
      { valeur: 'all', libelle: 'Tous' },
      { valeur: 'with', libelle: 'Avec' },
      { valeur: 'without', libelle: 'Sans' },
    ],
  },
  {
    cle: 'trajets',
    etiquette: 'Trajets',
    options: [
      { valeur: 'all', libelle: 'Tous' },
      { valeur: 'with', libelle: 'En trajet' },
      { valeur: 'without', libelle: 'Aucun' },
    ],
  },
];

/**
 * Un groupe segmenté : l'option active est le seul élément saturé de la barre,
 * en pétrole plein. Les autres restent en texte sourd — sans quoi trois
 * boutons bordés côte à côte se lisent comme trois actions à égalité, alors
 * qu'il s'agit d'un choix unique déjà fait.
 */
const GroupeSegmente = ({ etiquette, options, valeur, onChange }) => (
  <div className="flex items-center gap-2">
    <span className="shrink-0 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
      {etiquette} :
    </span>
    <div
      className="flex items-center gap-0.5 rounded-xl border border-border/70 bg-card p-1 shadow-sm"
      role="group"
      aria-label={etiquette}
    >
      {options.map((option) => {
        const actif = option.valeur === valeur;
        return (
          <button
            key={option.valeur}
            type="button"
            aria-pressed={actif}
            onClick={() => onChange(option.valeur)}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              actif
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {option.libelle}
          </button>
        );
      })}
    </div>
  </div>
);

const CompaniesPage = () => {
  const navigate = useNavigate();
  const { canSeeMoney, hasModule } = useAuth();
  const voitLots = typeof hasModule === 'function' ? hasModule('lots') : true;
  const [companies, setCompanies] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trips, setTrips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Le squelette ne remplace la page qu'au tout premier chargement:
  // un rafraîchissement démontait le champ de recherche en cours de frappe.
  const [hasLoaded, setHasLoaded] = useState(false);

  const [totalCompanies, setTotalCompanies] = useState(0);
  // Les chiffres de l'en-tête, comptés par le serveur sur toute la recherche:
  // la page reçue n'en montre qu'une tranche.
  const [serverCounts, setServerCounts] = useState(null);

  // Reste dû de chaque société de la page (un seul appel) et total général.
  const [soldes, setSoldes] = useState({});
  const [soldeTotal, setSoldeTotal] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');

  // La frappe reste immédiate à l'écran; seule la requête attend.

  const debouncedSearch = useDebouncedValue(searchQuery);
  const [filterTrucks, setFilterTrucks] = useState('all'); // all | with | without
  const [filterTrips, setFilterTrips] = useState('all'); // all | with | without

  // Pagination commune: page et taille dans l'adresse, barre sous la liste.
  // Une recherche ou un filtre ramène à la première page.
  const pagination = usePaginationServeur(totalCompanies, {
    reinitialiser: `${debouncedSearch}|${filterTrucks}|${filterTrips}`,
  });
  const { page, limit: pageSize } = pagination;

  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const [showNewModal, setShowNewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');

  const nouvelleDemande = useDerniereDemande();
  const loadData = useCallback(async () => {
    const perimee = nouvelleDemande();
    try {
      setIsLoading(true);
      // Le serveur applique la recherche et les deux filtres avant de découper
      // en pages: un filtre ne triait sinon que la page affichée.
      const companiesPage = await companiesAPI.getAllPaged({
        page,
        page_size: pageSize,
        q: debouncedSearch,
        ...(filterTrucks !== 'all' ? { with_trucks: filterTrucks === 'with' } : {}),
        ...(filterTrips !== 'all' ? { with_active_trips: filterTrips === 'with' } : {}),
      });
      if (perimee()) return;
      const companiesData = companiesPage.items || [];
      setCompanies(companiesData);
      setTotalCompanies(companiesPage.total ?? companiesData.length);
      setServerCounts(companiesPage.counts || null);

      if (!selectedCompanyId && companiesData.length > 0) {
        setSelectedCompanyId(companiesData[0].id);
      }
    } catch (err) {
      if (perimee()) return;
      console.error('Failed to load companies page data:', err);
      toast.error(messageErreur(err, 'Impossible de charger les sociétés'));
    } finally {
      if (!perimee()) {
        setIsLoading(false);
        setHasLoaded(true);
      }
    }
  }, [page, pageSize, debouncedSearch, filterTrucks, filterTrips, selectedCompanyId, nouvelleDemande]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Les soldes des comptes: un seul appel pour les sociétés de la page, et
  // seulement pour qui voit les montants. Un échec n'empêche pas la liste.
  const idsDeLaPage = companies.map((c) => c.id).join(',');
  useEffect(() => {
    if (!canSeeMoney || !idsDeLaPage) {
      setSoldes({});
      setSoldeTotal(null);
      return undefined;
    }
    let annule = false;
    partnerAccountAPI
      .soldes(idsDeLaPage.split(','))
      .then((reponse) => {
        if (annule) return;
        setSoldes(Object.fromEntries((reponse?.items || []).map((ligne) => [ligne.company_id, ligne])));
        setSoldeTotal(reponse?.total || null);
      })
      .catch((err) => {
        if (annule) return;
        console.error('Failed to load partner account balances:', err);
        setSoldes({});
        setSoldeTotal(null);
      });
    return () => {
      annule = true;
    };
  }, [idsDeLaPage, canSeeMoney]);

  // Les compteurs « camions » et « en trajet » de chaque société doivent
  // compter TOUT le parc et tous les trajets, pas les deux cents premiers:
  // une société dont les camions sont plus loin dans la liste paraissait vide.
  // Chargé une fois, pas à chaque frappe de la recherche des sociétés.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchAllPages((params) => trucksAPI.getAllPaged(params)),
      fetchAllPages((params) => tripsAPI.getAllPaged(params)),
    ])
      .then(([trucksResult, tripsResult]) => {
        if (cancelled) return;
        setTrucks(trucksResult.items || []);
        setTrips(tripsResult.items || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load companies fleet data:', err);
        toast.error(messageErreur(err, 'Impossible de charger les camions et les trajets'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const trucksByCompanyId = useMemo(() => {
    const map = new Map();
    for (const t of trucks) {
      const cid = t?.company_id;
      if (!cid) continue;
      if (!map.has(cid)) map.set(cid, []);
      map.get(cid).push(t);
    }
    return map;
  }, [trucks]);

  const activeTripsByCompanyId = useMemo(() => {
    const map = new Map();
    const truckCompany = new Map();
    for (const t of trucks) {
      if (t?.id && t?.company_id) truckCompany.set(t.id, t.company_id);
    }

    for (const trip of trips) {
      if (!trip?.truck_id) continue;
      const cid = truckCompany.get(trip.truck_id);
      if (!cid) continue;
      if (!isTripActive(trip.status)) continue;
      if (!map.has(cid)) map.set(cid, []);
      map.get(cid).push(trip);
    }
    return map;
  }, [trips, trucks]);

  const filteredCompanies = useMemo(() => {
    return companies.filter((c) => {
      const trucksCount = trucksByCompanyId.get(c.id)?.length || 0;
      const tripsCount = activeTripsByCompanyId.get(c.id)?.length || 0;

      if (filterTrucks === 'with' && trucksCount === 0) return false;
      if (filterTrucks === 'without' && trucksCount > 0) return false;
      if (filterTrips === 'with' && tripsCount === 0) return false;
      if (filterTrips === 'without' && tripsCount > 0) return false;

      return true;
    });
  }, [
    companies,
    filterTrucks,
    filterTrips,
    trucksByCompanyId,
    activeTripsByCompanyId,
  ]);

  // Une liste vide n'a pas la même signification selon qu'on a filtré ou non :
  // « il n'y a rien » demande de créer, « rien ne correspond » demande
  // d'effacer les filtres. Le test calculé une fois, lu quatre fois.
  const filtresActifs =
    Boolean(searchQuery) || filterTrucks !== 'all' || filterTrips !== 'all';

  useEffect(() => {
    if (!selectedCompanyId) {
      if (filteredCompanies.length > 0)
        setSelectedCompanyId(filteredCompanies[0].id);
      return;
    }

    const stillExists = companies.some((c) => c.id === selectedCompanyId);
    if (!stillExists) {
      setSelectedCompanyId(filteredCompanies[0]?.id || null);
    }
  }, [companies, filteredCompanies, selectedCompanyId]);

  const selectedCompany =
    companies.find((c) => c.id === selectedCompanyId) || null;
  const selectedCompanyTrucks = selectedCompanyId
    ? trucksByCompanyId.get(selectedCompanyId) || []
    : [];
  const selectedCompanyActiveTrips = selectedCompanyId
    ? activeTripsByCompanyId.get(selectedCompanyId) || []
    : [];
  // Chiffres du serveur quand il les donne (toute la recherche); sinon ceux de
  // la liste reçue, qui est alors la liste entière (hors ligne).
  const companiesWithTrucks =
    serverCounts?.avecCamions ??
    companies.filter((company) => (trucksByCompanyId.get(company.id)?.length || 0) > 0).length;
  const companiesWithActiveTrips =
    serverCounts?.trajetsActifs ??
    companies.filter((company) => (activeTripsByCompanyId.get(company.id)?.length || 0) > 0).length;
  const companiesWithoutTrucks =
    serverCounts?.aQualifier ?? Math.max(0, companies.length - companiesWithTrucks);
  const companiesAll = serverCounts
    ? serverCounts.avecCamions + serverCounts.aQualifier
    : companies.length;

  const handleCreateCompany = async () => {
    setSubmitAttempted(true);
    if (!newCompanyName.trim()) {
      toast.error('Indiquez le nom de la société');
      return;
    }

    setIsSubmitting(true);
    try {
      await companiesAPI.create({ name: newCompanyName.trim() });
      toast.success('Société ajoutée');
      setShowNewModal(false);
      setNewCompanyName('');
      setSubmitAttempted(false);
      await loadData();
    } catch (err) {
      console.error('Create company failed:', err);
      toast.error(messageErreur(err, 'Impossible d’ajouter la société'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!selectedCompanyId) return;

    setIsSubmitting(true);
    try {
      await companiesAPI.delete(selectedCompanyId);
      toast.success('Société supprimée');
      setShowDeleteModal(false);
      setSelectedCompanyId(null);
      await loadData();
    } catch (err) {
      console.error('Delete company failed:', err);
      // Une société qui loue des camions à la nôtre ne se supprime pas: le
      // message le dit et propose d'ouvrir ses contrats.
      const regle = err.response?.data?.error?.details?.rule;
      toast.error(messageErreur(err, 'Erreur lors de la suppression'), {
        action:
          regle === 'COMPANY_HAS_INBOUND_CONTRACTS'
            ? {
                label: 'Voir ses contrats',
                onClick: () =>
                  navigate(`/rentals?onglet=entrantes&societe=${selectedCompanyId}`),
              }
            : undefined,
      });
      setShowDeleteModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !hasLoaded) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="cards" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="companies-page">
      {/* Barre de recherche d'en-tête.
          Elle coiffe la zone de contenu comme sur la maquette et pilote la même
          requête que le champ de la barre de filtres : deux champs qui
          chercheraient séparément donneraient deux résultats contradictoires
          sur le même écran. Celui-ci est le point d'entrée — on arrive, on
          tape ; celui de la barre de filtres accompagne la liste une fois
          qu'on y travaille. Le `data-testid` reste sur le second, qui est
          celui que les filtres encadrent.
          Son libellé nomme ce qu'il sait faire — société, SIREN, contact — et
          non « rechercher » tout court: depuis que la barre supérieure de
          l'application porte la recherche transverse, un libellé vague ici
          laissait croire qu'on cherchait dans tout le produit. */}
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rechercher une société, un SIREN, un contact…"
          aria-label="Rechercher une société"
          className="input-base h-11 rounded-xl pl-10"
        />
      </div>

      <PageHero
        illustration="clients"
        statsDisposition="ligne"
        eyebrow="Réseau"
        title="Sociétés"
        description="Gérez votre réseau de partenaires transport en toute simplicité."
        stats={[
          { label: 'Sociétés', valeur: companiesAll, aide: 'Partenaires transport' },
          { label: 'Avec camions', valeur: companiesWithTrucks, aide: 'Capacité identifiée' },
          { label: 'Trajets actifs', valeur: companiesWithActiveTrips, ton: 'positif', aide: 'En exploitation' },
          {
            label: 'À qualifier',
            valeur: companiesWithoutTrucks,
            ton: companiesWithoutTrucks ? 'attention' : 'neutre',
            aide: 'Aucun camion lié',
          },
          ...(canSeeMoney && soldeTotal
            ? [
                statMontant({
                  label: 'Reste dû',
                  valeur: soldeTotal.balance,
                  ton: Number(soldeTotal.balance) > 0 ? 'attention' : 'neutre',
                  aide: 'Ce que les partenaires doivent encore à PKS',
                  testid: 'companies-stat-reste-du',
                }),
              ]
            : []),
        ]}
        actions={
          <Button
            onClick={() => setShowNewModal(true)}
            data-testid="new-company-btn"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nouvelle société
          </Button>
        }
      />

      {/* Barre de filtres : recherche puis les deux réductions possibles.
          Les deux groupes sont appliqués par le serveur avant la découpe en
          pages (`with_trucks`, `with_active_trips`); ici, le même croisement
          reste fait sur la page reçue pour le cache hors ligne. */}
      <div className="flex flex-col gap-3 rounded-2xl border border-border/70 bg-card p-3 shadow-sm lg:flex-row lg:flex-wrap lg:items-center">
        <div className="relative min-w-[220px] flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70"
            aria-hidden="true"
          />
          <Input
            placeholder="Rechercher une société…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-base pl-9"
            data-testid="companies-search"
          />
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          {GROUPES_FILTRE.map((groupe) => (
            <GroupeSegmente
              key={groupe.cle}
              etiquette={groupe.etiquette}
              options={groupe.options}
              valeur={groupe.cle === 'camions' ? filterTrucks : filterTrips}
              onChange={groupe.cle === 'camions' ? setFilterTrucks : setFilterTrips}
            />
          ))}
        </div>
      </div>

      {/* La liste. Chaque société est une cible de sélection : elle alimente le
          panneau de détail juste dessous, d'où la bordure pétrole sur
          l'élément retenu — c'est un état de sélection, pas une décoration. */}
      {filteredCompanies.length === 0 ? (
        <EmptyState
          variant={filtresActifs ? 'filtered' : 'empty'}
          illustration={filtresActifs ? undefined : 'donnee'}
          title={filtresActifs ? 'Aucun résultat' : 'Aucune société enregistrée'}
          description={
            filtresActifs
              ? 'Aucune société ne correspond à la recherche ou aux filtres actifs. Vos sociétés sont toujours là : effacez les filtres pour les revoir.'
              : 'Une société est le propriétaire des camions et des trajets. Créez-en une par entité qui facture séparément — c’est elle qui porte les camions, les chauffeurs et les documents.'
          }
          action={
            filtresActifs ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setFilterTrucks('all');
                  setFilterTrips('all');
                }}
              >
                Effacer les filtres
              </Button>
            ) : (
              <Button size="sm" onClick={() => setShowNewModal(true)}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Ajouter une société
              </Button>
            )
          }
        />
      ) : (
        <SectionCard
          icon={Building2}
          title="Réseau de sociétés"
          subtitle={`${filteredCompanies.length} société${filteredCompanies.length > 1 ? 's' : ''} affichée${filteredCompanies.length > 1 ? 's' : ''}`}
          contentClassName="pt-3"
        >
          <ul
            className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3"
            data-testid="companies-strip"
          >
            {filteredCompanies.map((c) => {
              const trucksCount = trucksByCompanyId.get(c.id)?.length || 0;
              const tripsCount = activeTripsByCompanyId.get(c.id)?.length || 0;
              const isSelected = c.id === selectedCompanyId;
              const solde = soldes[c.id];

              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedCompanyId(c.id)}
                    aria-pressed={isSelected}
                    className={cn(
                      'w-full rounded-xl border bg-card px-3.5 py-3 text-left transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border/70 hover:border-primary/40 hover:bg-muted/50',
                    )}
                  >
                    <span className="block truncate text-sm font-bold text-foreground">
                      {c.name}
                    </span>
                    <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Truck className="h-3.5 w-3.5" aria-hidden="true" />
                        <span className="tabular-nums">{trucksCount}</span> camions
                      </span>
                      {/* « En trajet » est un état, et le seul de cette ligne
                          qui mérite d'être teinté ; zéro trajet n'est pas une
                          anomalie, il reste donc sourd. */}
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5',
                          tripsCount > 0 && 'font-semibold text-success',
                        )}
                      >
                        <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                        <span className="tabular-nums">{tripsCount}</span> en trajet
                      </span>
                    </span>
                    {solde && Number(solde.operations_count) > 0 ? (
                      <span
                        className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
                        data-testid={`company-solde-${c.id}`}
                      >
                        <StatutBadge domaine="situation_compte" code={solde.situation} />
                        <span
                          className={cn(
                            'font-semibold tabular-nums',
                            Number(solde.balance) < 0 && 'text-destructive',
                          )}
                        >
                          {formatMontant(solde.balance)}
                        </span>
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </SectionCard>
      )}

      {filteredCompanies.length > 0 ? <BarrePagination pagination={pagination} nom="sociétés" /> : null}

      {/* Détail de la société retenue. */}
      {selectedCompany ? (
        <section className="rounded-2xl border border-border/70 bg-card shadow-sm">
          <header className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 md:px-5 md:pt-5">
            <div className="min-w-0">
              <h2 className="truncate text-base font-bold leading-tight text-foreground">
                {selectedCompany.name}
              </h2>
              <p className="truncate text-xs text-muted-foreground">
                <span className="tabular-nums">{selectedCompanyTrucks.length}</span> camions •{' '}
                <span className="tabular-nums">{selectedCompanyActiveTrips.length}</span> trajets en cours
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
            {canSeeMoney ? (
              <Button
                size="sm"
                onClick={() => navigate(`/companies/${selectedCompany.id}/compte`)}
                data-testid="company-account-btn"
              >
                <Wallet className="h-4 w-4" aria-hidden="true" />
                Compte
              </Button>
            ) : null}
            {voitLots ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/lots/liste?transporteur=${selectedCompany.id}`)}
                data-testid="company-lots-btn"
              >
                <Layers className="h-4 w-4" aria-hidden="true" />
                Lots attribués
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(`/rentals?onglet=entrantes&societe=${selectedCompany.id}`)
              }
              data-testid="company-inbound-contracts-btn"
            >
              <KeyRound className="h-4 w-4" aria-hidden="true" />
              Contrats de location reçue
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="shrink-0"
              aria-label="Supprimer la société"
              onClick={() => setShowDeleteModal(true)}
              data-testid="delete-company-btn"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
            </div>
          </header>

          <div className="px-4 pb-4 pt-4 md:px-5 md:pb-5">
            <Tabs defaultValue="trips">
              <TabsList className="w-full">
                <TabsTrigger value="trips" className="flex-1">
                  <MapIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                  En trajet
                </TabsTrigger>
                <TabsTrigger value="trucks" className="flex-1">
                  <Truck className="mr-2 h-4 w-4" aria-hidden="true" />
                  Camions
                </TabsTrigger>
              </TabsList>

              <TabsContent value="trips" className="mt-3">
                {selectedCompanyActiveTrips.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                    Aucun trajet en cours
                  </p>
                ) : (
                  <div className="space-y-2">
                    {selectedCompanyActiveTrips.slice(0, 50).map((trip) => {
                      const truck = trucks.find((t) => t.id === trip.truck_id);
                      return (
                        <div
                          key={trip.id}
                          className="flex items-center justify-between gap-3 rounded-xl border border-border/70 px-3 py-2"
                        >
                          <div className="min-w-0">
                            <div className="truncate font-mono text-sm font-bold text-foreground">
                              {truck?.matricule || 'Camion non précisé'}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {nomPays(trip.departure_country)} →{' '}
                              {nomPays(trip.destination_country)}
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge
                              className={cn(
                                'status-badge',
                                getStatusBadgeClass(trip.status)
                              )}
                            >
                              {libelle('tripStatus', trip.status)}
                            </Badge>
                            <div className="mt-1 text-xs text-muted-foreground">
                              {formatRelativeTime(trip.updated_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="trucks" className="mt-3">
                {selectedCompanyTrucks.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                    Aucun camion lié
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {selectedCompanyTrucks.slice(0, 100).map((t) => (
                      <div
                        key={t.id}
                        className="rounded-xl border border-border/70 bg-card px-3 py-2"
                      >
                        <div className="matricule-plate text-sm">
                          {t.matricule}
                        </div>
                        {t.tank_number ? (
                          <div className="mt-1 text-xs text-muted-foreground">
                            Citerne: {t.tank_number}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </section>
      ) : null}

      {/* New Company Modal */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle société</DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            <Label className="label-base">Nom *</Label>
            <Input
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              className="input-base"
              placeholder="Ex: Sidi Logistique"
              data-testid="company-name-input"
            />
            <FieldError
              show={submitAttempted && !newCompanyName.trim()}
              className="text-xs"
            >
              Champ requis
            </FieldError>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNewModal(false)}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              className="btn-primary"
              onClick={handleCreateCompany}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'En cours...' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <AlertDialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la société ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les camions liés seront conservés, mais ne seront plus associés à
              cette société.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCompany}
              disabled={isSubmitting}
              className="bg-destructive hover:bg-destructive"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};


export default CompaniesPage;
